const SETTINGS_PREFIX = '/settings';

const APP_PREFIXES = [
  '/dashboard',
  '/employee-dashboard',
  '/employee-portal',
  '/portfolio',
  '/create-business',
  '/admin',
  '/manage-plan',
  '/select-plan',
];

let returnPath: string | null = null;

function isAppPath(path: string): boolean {
  const pathname = path.split('?')[0];
  return APP_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** Keep the last non-settings page. Switching between settings screens must not overwrite it. */
export function rememberSettingsOrigin(pathname: string, search: string) {
  if (pathname.startsWith(SETTINGS_PREFIX)) return;
  returnPath = `${pathname}${search}`;
}

/**
 * Where closing settings goes. Public pages are dropped so a bad history entry
 * cannot leave the dashboard for the marketing site.
 */
export function consumeSettingsOrigin(): string {
  const dest = returnPath;
  returnPath = null;
  if (dest && isAppPath(dest)) return dest;
  return '/dashboard';
}

export function resetSettingsOrigin() {
  returnPath = null;
}

const BUSINESS_SECTIONS = ['business', 'hours', 'staff', 'reviews', 'integrations', 'features', 'danger'] as const;

export type CanonicalBusinessSection = (typeof BUSINESS_SECTIONS)[number];

const BUSINESS_SECTION_ALIASES: Record<string, CanonicalBusinessSection> = {
  'business-info': 'business',
  financial: 'business',
  'time-tracking': 'staff',
  'employee-shifts': 'staff',
  'feature-visibility': 'features',
  account: 'danger',
};

/** Current section id, or the section an old link should open. */
export function canonicalBusinessSection(raw: string | null): CanonicalBusinessSection {
  if (raw && (BUSINESS_SECTIONS as readonly string[]).includes(raw)) {
    return raw as CanonicalBusinessSection;
  }
  if (raw && BUSINESS_SECTION_ALIASES[raw]) return BUSINESS_SECTION_ALIASES[raw];
  return 'business';
}

/** New business-settings URL when already on that screen, otherwise null. */
export function businessSettingsPath(pathname: string, search: string, businessId: string): string | null {
  if (!pathname.startsWith('/settings/business/')) return null;
  const section = canonicalBusinessSection(new URLSearchParams(search).get('section'));
  return `/settings/business/${businessId}?section=${section}`;
}
